import React from "react";
import { Button } from "@/components/ui/button";
import { PencilSimple, Trash, Warning, Scales } from "@phosphor-icons/react";
import { formatIdDate } from "@/lib/format";

/**
 * SalesOrdersTable — presentational table for the SO list.
 * Sort/filter live in the parent; this component just renders rows + arrows.
 */
export default function SalesOrdersTable({
  items,
  totalRawCount,
  alerts,
  plants,
  products,
  districts,
  sortMode,
  onToggleSort,
  onEdit,
  onDelete,
  onAdjust,
}) {
  const plantName = (id) => plants.find((p) => p.id === id)?.nama || "-";
  const productName = (id) => products.find((p) => p.id === id)?.nama || "-";
  const districtLabel = (id) => {
    if (!id) return "— Semua —";
    const d = districts.find((x) => x.id === id);
    return d ? `${d.kode}` : "-";
  };

  const arrow = (col) => {
    const map = {
      date: ["date_desc", "date_asc"],
      no_so: ["no_so_desc", "no_so_asc"],
      plant: ["plant_desc", "plant_asc"],
      produk: ["produk_desc", "produk_asc"],
    };
    const [desc, asc] = map[col] || [];
    if (sortMode === desc) return " ▼";
    if (sortMode === asc) return " ▲";
    return "";
  };

  return (
    <div className="border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]" data-testid="so-table">
        <thead className="border-b border-border bg-secondary/50 text-left">
          <tr>
            <th
              className="p-3 overline cursor-pointer select-none hover:bg-secondary/80 whitespace-nowrap"
              onClick={() => onToggleSort("no_so")}
              data-testid="so-sort-no-so"
              title="Klik: urutkan No SO A-Z / Z-A"
            >
              No SO{arrow("no_so")}
            </th>
            <th
              className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
              onClick={() => onToggleSort("plant")}
              data-testid="so-sort-plant"
              title="Klik: urutkan Plant A-Z / Z-A"
            >
              Plant{arrow("plant")}
            </th>
            <th
              className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
              onClick={() => onToggleSort("produk")}
              data-testid="so-sort-produk"
              title="Klik: urutkan Produk A-Z / Z-A"
            >
              Produk{arrow("produk")}
            </th>
            <th className="p-3 overline">Distrik</th>
            <th
              className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
              onClick={() => onToggleSort("date")}
              data-testid="so-sort-terbit"
              title="Klik: urutkan tanggal terbaru / terlama"
            >
              Terbit{arrow("date")}
            </th>
            <th className="p-3 overline">Total</th>
            <th className="p-3 overline">Sisa</th>
            <th className="p-3 overline">Status</th>
            <th className="p-3 overline">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {items.map((so) => {
            const alertMatch = alerts.find((a) => a.produk_id === so.produk_id);
            const critical = !!alertMatch;
            const daysUntilExpiryClass = so.is_expired
              ? "text-red-600 font-bold"
              : so.days_until_expiry <= 2
                ? "text-orange-600 font-semibold"
                : "text-muted-foreground";
            return (
              <tr
                key={so.id}
                className={`border-b border-border hover:bg-secondary/50 ${critical ? "bg-red-50/40" : ""}`}
                data-testid={`so-row-${so.no_so}`}
              >
                <td className="p-3 font-mono font-bold">{so.no_so}</td>
                <td className="p-3">{plantName(so.plant_id)}</td>
                <td className="p-3">
                  {productName(so.produk_id)}
                  {critical && <Warning size={12} weight="bold" className="inline ml-1 text-red-600" />}
                </td>
                <td className="p-3 font-mono text-xs">
                  {so.distrik_id ? (
                    <span className="border border-border px-1.5 py-0.5">{districtLabel(so.distrik_id)}</span>
                  ) : (
                    <span className="text-muted-foreground italic">semua</span>
                  )}
                </td>
                <td className="p-3 font-mono text-xs">
                  {formatIdDate(so.tanggal_terbit)}
                  {so.days_until_expiry !== null && so.days_until_expiry !== undefined && (
                    <div className={`text-[10px] mt-0.5 uppercase tracking-wider ${daysUntilExpiryClass}`}>
                      {so.is_expired
                        ? `expired ${-so.days_until_expiry} hr lalu`
                        : `sisa ${so.days_until_expiry} hr`}
                    </div>
                  )}
                </td>
                <td className="p-3 font-mono">{so.kuantiti_total}</td>
                <td className={`p-3 font-mono font-bold ${critical ? "text-red-600" : ""}`}>{so.kuantiti_sisa}</td>
                <td className="p-3">
                  {so.is_expired ? (
                    <span
                      className="status-pill"
                      style={{ background: "#7f1d1d", color: "#fff" }}
                      data-testid={`so-status-expired-${so.no_so}`}
                    >
                      EXPIRED
                    </span>
                  ) : (
                    <span className={`status-pill ${so.aktif ? "status-termuat" : "status-belum"}`}>
                      {so.aktif ? "AKTIF" : "NONAKTIF"}
                    </span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      onClick={() => onEdit(so)}
                      data-testid={`edit-so-${so.no_so}`}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      onClick={() => onAdjust(so)}
                      title="Penyesuaian kuantiti (butuh approval Super Admin)"
                      data-testid={`adjust-so-${so.no_so}`}
                    >
                      <Scales size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg text-destructive bg-destructive/5 border-destructive/40"
                      onClick={() => onDelete(so)}
                      data-testid={`delete-so-${so.no_so}`}
                      title="Hapus permanen"
                    >
                      <Trash size={14} weight="fill" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
          {totalRawCount === 0 && (
            <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Belum ada SO.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
